import { clone, element, getElementById } from "../util/Utils";
import { registerFilterFormListeners } from "./FilterFormActions";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { OverflowBox } from "../overflowBox/OverflowBox";
import { FacetValueTag } from "./FacetValueTag";
import { referencedFeature, updateLabel } from "../tracking/FeatureTracking";
import type { Appendable } from "../util/Appendable";
import { events } from "../events";
import { SvelteFacetValue } from "./SvelteFacetValue";

const CLASS_FACET_VALUE_TAGS = "find_facetValueTags";
const SELECTOR_FACET_VALUE_TAGS = `.${CLASS_FACET_VALUE_TAGS}`;
const CLASS_DESELECT_FACET_VALUE_TAGS = "find_facetValueTag__deselectAll";
const SELECTOR_DESELECT_FACET_VALUE_TAGS = `.${CLASS_DESELECT_FACET_VALUE_TAGS}`;
const SELECTOR_TEMPLATE_FACET_VALUE_TAGS = `#san_facetValueTags ${SELECTOR_FACET_VALUE_TAGS}`;

export class FacetValueTags implements Appendable {
  static readonly factory = HeurekaElementFactory.byClass(CLASS_FACET_VALUE_TAGS, FacetValueTags);

  /*               */

  constructor(readonly tags: HTMLElement) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return FacetValueTags.factory.pick(SELECTOR_TEMPLATE_FACET_VALUE_TAGS, rootElement);
  }

  /*               */

  static register() {
    events.onFilterSectionLoaded(FacetValueTags.initAll);
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    FacetValueTags.emitSelectedFacetValues(event.rule, rootElement);
    FacetValueTags.factory.forEach((form) => form.init(), rootElement);
  }

  protected init() {
    registerFilterFormListeners(this.tags);

    /*                                                         */
    this.facetValueTags.forEach((tag) =>
      tag.facetValueTag.addEventListener("click", () =>
        tag.updateTrackingLabelsOnReferenced("deleted", "x_single_top", "list_initial"),
      ),
    );
    this.deselectAllButton?.addEventListener("click", () => {
      this.facetValueTags.forEach((tag) =>
        tag.updateTrackingLabelsOnReferenced("deleted", "x_all_top", "list_initial"),
      );
    });
    return this;
  }

  /*                       */

  get facetValueTags(): FacetValueTag[] {
    return FacetValueTag.factory.all(this.tags);
  }

  get deselectAllButton(): HTMLElement | null {
    return element(SELECTOR_DESELECT_FACET_VALUE_TAGS, this.tags);
  }

  appendTo(elem: HTMLElement): void {
    elem.appendChild(this.tags);
    this.recomputeSize();
    this.updateTrackingLabels();
  }

  clone() {
    return FacetValueTags.factory.create(clone(this.tags)).init();
  }

  /*                 */

  private static emitSelectedFacetValues(rule: string, rootElement?: ParentNode) {
    const facetValueTags = FacetValueTags.template(rootElement)?.facetValueTags;
    if (facetValueTags) {
      const selectedValues = facetValueTags.map((t) => t.title).flatMap((f) => (f ? [f] : []));
      events.facetValueTagsLoaded(rule, selectedValues);
    }
  }

  /*       */

  private static updateVisibilityTrackingLabels(facetValueTag: FacetValueTag, index: number) {
    const deselect = facetValueTag.deselect;
    if (deselect) {
      const selectedValuesIds = facetValueTag.selectedValuesIds;
      if (selectedValuesIds && selectedValuesIds.length > 0) {
        selectedValuesIds.forEach((id) => {
          const facetValue = getElementById(id);
          if (facetValue) {
            updateLabel(facetValue, "san_FacetValueTagPosition", (index + 1).toString());
          }
        });
      }
      const facetValue = referencedFeature(deselect);
      if (facetValue) {
        updateLabel(facetValue, "san_FacetValueTagPosition", (index + 1).toString());
      }
    }
  }

  private updateTrackingLabels() {
    FacetValueTag.factory.forEach(FacetValueTags.updateVisibilityTrackingLabels, this.tags);
  }

  recomputeSize() {
    OverflowBox.factory.declare(this.tags)?.initializeCollapsedState(true);
  }

  findIndexOf(predicate: (facetValueTag: FacetValueTag) => boolean): number {
    return this.facetValueTags.findIndex(predicate);
  }

  removeFacetValueTagsOf(predicate: (facetValueTag: FacetValueTag) => boolean): void {
    this.facetValueTags.filter(predicate).forEach((facetValueTag) => facetValueTag.facetValueTag.remove());
  }

  appendFacetValues(values: SvelteFacetValue[], index?: number) {
    let appendPosition = index !== undefined ? index : this.tags.children.length;
    values.forEach((value) => {
      const liElem = document.createElement("li");
      liElem.classList.add("find_facetValueTags__item", "heureka_overflowBox__item");
      liElem.append(value.label);
      this.tags.insertBefore(liElem, this.tags.children[appendPosition]);
      appendPosition++;
    });
  }
}
